import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import ProfileHeader from "./profileHeader";
import ProfileContactInfo from "./profileContactInfo";
import ProfileAddress from "./profileAddress";
import ProfileSalaryInfo from "./profileSalaryInfo";
import ProfileFeedback from "./profileFeedback";
import PasswordChangeForm from "./passwordChnageForm";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { markUpdatesAsSeen } from "../../redux/slice/sseSlicer";

// ── Helper: persist updated salary_details back to localStorage ──
const persistSalaryToStorage = (mergedArray) => {
  try {
    const raw = localStorage.getItem("adminData");
    if (!raw) return;

    const stored = JSON.parse(raw);
    const info = stored.data || stored;                         // handle both shapes
    info.salary_details = JSON.stringify(mergedArray);          // store as JSON string (matches DB format)

    if (stored.data) stored.data = info;
    else Object.assign(stored, info);

    localStorage.setItem("adminData", JSON.stringify(stored));
  } catch (e) {
    console.error("Failed to persist salary details to localStorage:", e);
  }
};

const ProfileDetails = () => {
  const adminDataString = localStorage.getItem("adminData");
  const adminData = adminDataString ? JSON.parse(adminDataString) : null;
  const adminInfo = adminData?.data || adminData;

  const dispatch = useDispatch();
  const liveData = useSelector((state) => state.sse.liveData);

  const initialRender = useRef(true);
  const lastFeedback = useRef(adminInfo?.feedback);
  const lastSalaryDetails = useRef(adminInfo?.salary_details);
  const [hasNewFeedback, setHasNewFeedback] = useState(false);

  const isSuperAdmin = adminInfo?.role === "super_admin";

  useEffect(() => {
    dispatch(markUpdatesAsSeen());
  }, [dispatch]);

  useEffect(() => {
    const storedFeedback = localStorage.getItem("lastSeenFeedback");

    if (liveData.aid && String(liveData.aid) !== String(adminInfo?.aid)) return;

    if (initialRender.current) {
      initialRender.current = false;
      lastSalaryDetails.current = liveData.salary_details ?? adminInfo?.salary_details;
      if (!isSuperAdmin && liveData.feedback && liveData.feedback !== storedFeedback) {
        setHasNewFeedback(true);
      }
      return;
    }

    if (!isSuperAdmin && liveData.feedback && liveData.feedback !== lastFeedback.current) {
      toast.info(`📝 New Feedback: ${liveData.feedback}`);
      lastFeedback.current = liveData.feedback;
      setHasNewFeedback(true);
    }

    if (
      liveData.salary_details !== null &&
      JSON.stringify(liveData.salary_details) !== JSON.stringify(lastSalaryDetails.current)
    ) {
      toast.success("💰 Salary record updated!");
      lastSalaryDetails.current = liveData.salary_details;
    }
  }, [liveData, adminInfo, isSuperAdmin]);

  // ── Parse existing history from localStorage ──
  const existingDetails = (() => {
    try {
      const raw = adminInfo?.salary_details;
      return Array.isArray(raw) ? raw : JSON.parse(raw || "[]");
    } catch { return []; }
  })();

  // ── Merge new SSE entry + persist so refresh keeps it ──
  const mergedSalaryDetails = (() => {
    if (!liveData.salary_details) return existingDetails;

    const newEntry = liveData.salary_details;
    const alreadyExists = existingDetails.some(
      (e) => e.full_date === newEntry.full_date
    );
    if (alreadyExists) return existingDetails;

    const merged = [...existingDetails, newEntry];

    persistSalaryToStorage(merged);   // ✅ write back so refresh survives

    return merged;
  })();

  const combinedData = {
    ...adminInfo,
    salary_details: mergedSalaryDetails,
  };

  const handleFeedbackSeen = () => {
    setHasNewFeedback(false);
    if (combinedData.feedback) {
      localStorage.setItem("lastSeenFeedback", combinedData.feedback);
    }
    dispatch(markUpdatesAsSeen());
  };

  if (!adminInfo) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">No profile data available</Typography>
        <Typography variant="body1">Please navigate back and try again</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
      <Box sx={{ flex: 1 }}>
        <ProfileHeader adminInfo={combinedData} />
        <Divider sx={{ my: 2 }} />
        <Box>
          <PasswordChangeForm adminContact={combinedData.admin_contact} />
        </Box>
        <Stack spacing={3}>
          <ProfileContactInfo
            contact={combinedData.admin_contact}
            phone={combinedData.phone}
          />

          {combinedData.address && <ProfileAddress address={combinedData.address} />}

          {combinedData.salary && (
            <ProfileSalaryInfo salary={combinedData.salary} />
          )}

          {!isSuperAdmin && combinedData.salary_details?.length > 0 && (
            <ProfileFeedback
              feedback={combinedData.salary_details}
              isNew={hasNewFeedback}
              onSeen={handleFeedbackSeen}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ProfileDetails;